export interface HelpStep {
  number: number;
  title: string;
  description: string;
}

export interface SectionLink {
  sectionId: string;
  title: string;
  description: string;
}

export interface HelpContent {
  title: string;
  overview: string;
  steps?: HelpStep[];
  tips?: string[];
  sectionLinks?: SectionLink[];
  notes?: string;
}

export const helpContent: Record<string, HelpContent> = {
  'splash': {
    title: 'Welcome to Campaign Creator',
    overview: 'You bring the creativity to deliver your marketing objectives and we will handle the configuration for you. Just work through the design screens to create your campaign.',
    sectionLinks: [
      {
        sectionId: 'feature-selection',
        title: 'Feature Selection',
        description: 'Choose how your audience will discover and interact with your campaign. Select from instant, location-based, quiz, or other engagement methods.'
      },
      {
        sectionId: 'ar-preview',
        title: 'AR Preview',
        description: 'Set the 3D model shown in your AR scene. Upload your own model or browse defaults.'
      },
      {
        sectionId: 'map-preview',
        title: 'Map Preview',
        description: 'If you selected location-based campaigns, choose the map marker icon shown on the map screen.'
      },
      {
        sectionId: 'location-config',
        title: 'Location Settings',
        description: 'Define where your location-based experience is available. Pick a radius around a point or outline a custom Geo Fence area.'
      },
      {
        sectionId: 'voucher-config',
        title: 'Vouchers',
        description: 'Upload voucher codes via CSV or generate a voucher list and use it in your campaign.'
      },
      {
        sectionId: 'look-feel',
        title: 'Look & Feel',
        description: 'Customise your campaign\'s appearance by uploading brand assets (logos, map markers, 3D models) and setting your brand colours.'
      },
      {
        sectionId: 'message',
        title: 'Prize Message',
        description: 'Set the congratulatory message winners see when they claim a prize.'
      },
      {
        sectionId: 'schedule',
        title: 'Schedule',
        description: 'Set your campaign start and end dates. Configure timezone settings and campaign duration.'
      },
      {
        sectionId: 'preview-experience',
        title: 'Preview Experience',
        description: 'Review how your campaign will appear to end users before finalizing your setup.'
      },
      {
        sectionId: 'user-details',
        title: 'User Details',
        description: 'Provide your contact information to finalize your campaign setup.'
      }
    ],
    tips: [
      'You can click the help button at any time to get contextual guidance for the section you\'re viewing.',
      'Progress is saved automatically as you complete each section.',
      'Use the preview section to see exactly how your campaign will look before submitting.'
    ]
  },
  'feature-selection': {
    title: 'Experience Selection',
    overview: 'Select where people can play and the type of experience that determines how users will interact with your campaign.',
    tips: [
      'You can change your selection at any time before finalizing your campaign.',
      'The experience you choose will determine the template options available in later sections.',
      'Consider your target audience and how they typically discover new experiences.'
    ],
    notes: 'After selecting your experience setup, you\'ll continue through the next configuration pages where you will add design elements, rewards, links etc'
  },
  'look-feel': {
    title: 'Registration & Enter Pin',
    overview: 'Customise the registration screen users see before starting. Set the logo, apply a colour palette, and adjust text, button, and background colours or upload images.',
    steps: [
      {
        number: 1,
        title: 'Customise the Page',
        description: 'You can change the page background colour or upload your own image, logo, text colour, and customise the button effects and settings.'
      },
      {
        number: 2,
        title: 'Use the Phone Preview',
        description: 'Tap the different areas in the phone preview to change that section\'s settings where you can choose colours, images and change the wording and other customizable settings.'
      },
      {
        number: 3,
        title: 'Review the Screen Preview',
        description: 'Confirm the registration page looks consistent with your campaign style before continuing.'
      }
    ],
    tips: [
      'Use high-contrast combinations for text readability.',
      'Keep logo artwork simple and centered for small screens.',
      'You can re-open each tap target and iterate before submission.',
      'If you can\'t click the next button, it means something still needs to be changed.',
      'The Enter Pin page shares the same configuration as the registration page.'
    ],
    notes: 'Updates are reflected in real time, so you can tune colours and branding as you go.'
  },
  'ar-preview': {
    title: 'AR Preview',
    overview: 'Set the 3D model used in the Augmented Reality section of your campaign. Users tap it to claim their reward.',
    steps: [
      {
        number: 1,
        title: 'Open AR Asset Actions',
        description: 'Tap the AR phone preview to open asset actions to browse default options or upload your own.'
      }
    ],
    tips: [
      'Use optimized `.glb`/`.gltf` files for best runtime performance.',
      'Models above 10 MB can increase load times on mobile or fail to upload.'
    ]
  },
  'map-preview': {
    title: 'Map Marker Preview',
    overview: 'For location-based campaigns, choose the map marker users will see on the map view.',
    steps: [
      {
        number: 1,
        title: 'Open Marker Actions',
        description: 'Tap the map preview phone to choose marker actions from the default options or upload your own.'
      }
    ],
    tips: [
      'Simple marker shapes work best at small sizes.',
      'Use transparent PNGs for cleaner marker edges.'
    ]
  },
  'message': {
    title: 'Prize Message',
    overview: 'Create a congratulatory message that winners will see when they successfully claim a prize. This short message adds excitement and personal branding to the winning moment.',
    steps: [
      {
        number: 1,
        title: 'Enter Your Message',
        description: 'Type your message in the text field. It should be short, engaging, and friendly.'
      },
      {
        number: 2,
        title: 'Check the Character Count',
        description: 'Keep an eye on the character limit. The counter will turn yellow as you approach the limit and red when you reach it.'
      }
    ],
    tips: [
      'Keep it brief and enthusiastic (e.g., "Congratulations! You won!").',
      'Use your brand voice to make the message feel personal.',
      'Simple, clear language works best for mobile users.',
      'The system will automatically prevent profanity or politically charged content.'
    ],
    notes: 'This message is shown to every winner on their claim screen, so make sure it reflects your campaign\'s tone.'
  },
  'location-config': {
    title: 'Location Settings',
    overview: 'Define where your campaign will be active and discoverable to your audience. Choose a radius for a simple area, or use Geo Fence for detailed coverage.',
    steps: [
      {
        number: 1,
        title: 'Choose a Location Mode',
        description: 'Select Radius for a simple circular area or Geo Fence for a specific area or location. You can switch modes at any time before submitting.'
      },
      {
        number: 2,
        title: 'Find the Area',
        description: 'Use the search bar to locate a city, address, or venue. Select your desired location from the suggestions provided.'
      },
      {
        number: 3,
        title: 'Adjust the Coverage',
        description: 'In Radius mode, drop the pin and use the slider to set the distance. In Geo Fence mode, confirm the outline and ensure it covers the desired area.'
      },
      {
        number: 4,
        title: 'Review the Map Preview',
        description: 'Check the highlighted area and zoom controls to ensure the experience will trigger in the right places.'
      }
    ],
    tips: [
      'Use Radius for quick setups, like a City area or events, with a clear centre point.',
      'Use Geo Fence for neighbourhoods, campuses, or specific areas that need precise boundaries.(BETA)'
    ],
    notes: 'Location settings only apply to location-based campaigns. Make sure your selected area matches where users will physically engage with the experience.'
  },
  'voucher-config': {
    title: 'Voucher Setup',
    overview: 'Add the voucher codes for this campaign. You can upload your own CSV file or generate voucher codes from the generator option.',
    steps: [
      {
        number: 1,
        title: 'Choose CSV Upload',
        description: 'Click "Choose CSV File" and upload a CSV with exactly one column and the header "Vouchers".'
      },
      {
        number: 2,
        title: 'Use Generator (Optional)',
        description: 'Open "Generate CSV" if you want the platform to create voucher codes for you instead of uploading an existing list if you do not have one.'
      },
      {
        number: 3,
        title: 'Review Voucher Preview',
        description: 'Confirm the preview list and total count are correct before continuing to the next section.'
      }
    ],
    tips: [
      'CSV format is strict: one column only, header must be exactly "Vouchers" with the list of vouchers in a list below.',
      'Remove blank or extra comma-separated values before upload to avoid parse errors.',
      'Use a short code prefix and longer code length when generating to reduce collision risk.'
    ],
    notes: 'Template CSV download is available in this help panel.'
  },
  'schedule': {
    title: 'Campaign Scheduling',
    overview: 'Set when your campaign will start and end. Configure the dates, times to ensure your campaign launches and concludes at the right moments.',
    steps: [
      {
        number: 1,
        title: 'Set Start Date and Time',
        description: 'Use the date and time pickers to select when your campaign should begin. The system will show you the scheduled start time in your local timezone.'
      },
      {
        number: 2,
        title: 'Set End Date and Time',
        description: 'Choose when your campaign should end. Make sure the end date is after the start date. Campaigns run for 30 days by default.'
      },
      {
        number: 3,
        title: 'Review Timezone',
        description: 'The system automatically detects your local timezone'
      },
      {
        number: 4,
        title: 'Check Countdown',
        description: 'Review the "Time Until Launch" display to confirm your campaign will start and end at the intended time.'
      }
    ],
    tips: [
      'Campaigns can be scheduled to start immediately or in the future.',
      'You can edit the schedule before submitting your campaign.'
    ],
    notes: 'Once your campaign is submitted, it will automatically launch at the scheduled start time. You\'ll receive notifications when the campaign goes live.'
  },
  'preview-experience': {
    title: 'Preview Your Campaign',
    overview: 'Review how your campaign will appear to end users. This preview shows the complete experience including your assets, colours, and campaign flow.',
    steps: [
      {
        number: 1,
        title: 'Review Visual Elements',
        description: 'Check that your logo, map markers, and 3D Objects appear correctly. Verify that colours match your brand identity.'
      },
      {
        number: 2,
        title: 'Test User Flow',
        description: 'Navigate through the preview to see how users will interact with your campaign. Check the discovery method, engagement points, and redemption flow.'
      },
      {
        number: 3,
        title: 'Verify Campaign Settings',
        description: 'Confirm that all your selections (feature, service, assets, colours, schedule) are reflected correctly in the preview.'
      },
      {
        number: 4,
        title: 'Make Final Adjustments',
        description: 'If anything doesn\'t look right, use the back navigation or help button to return to previous sections and make changes.'
      }
    ],
    tips: [
      'The preview shows exactly what users will see, so review it carefully.',
      'Test the preview on different screen sizes if possible.',
      'Make sure all text is readable and colours have good contrast.',
      'Verify that your 3D models load and display correctly.'
    ],
    notes: 'This is your last chance to review everything before submitting. Once you\'re satisfied with the preview, proceed to the User Details section to finalize your campaign.'
  },
  'user-details': {
    title: 'Complete Your Details',
    overview: 'Provide your contact information to finalize your campaign setup, communication and payment, fill in all required fields.',
    steps: [
      {
        number: 1,
        title: 'Enter Personal Information',
        description: 'Fill in your first name, surname, email address, and mobile number. All fields are required for account creation and campaign management.'
      },
      {
        number: 2,
        title: 'Review Consent Options',
        description: 'Read and accept the required consent checkboxes. Marketing consent is optional but helps us provide you with relevant updates.'
      },
      {
        number: 3,
        title: 'Submit Your Campaign',
        description: 'Once all fields are completed and validated, click the submit button to finalize your campaign setup. You\'ll receive a confirmation email once processing begins.'
      }
    ],
    tips: [
      'Use a valid email address as you\'ll receive important campaign updates there.',
      'Mobile numbers get their country code from the country selector you dont have to add it manually.',
      'All personal information is handled according to our privacy policy.'
    ],
    notes: 'After submitting, your campaign will be processed and launched according to your schedule. You\'ll receive email notifications at key milestones.'
  },
  'template-choice': {
    title: 'Choose Your Subscription',
    overview: 'Start with the free option to explore the platform, or choose a paid subscription for more features and flexibility. All billing is handled securely through Stripe.',
    steps: [
      {
        number: 1,
        title: 'Review Your Options',
        description: 'The free plan gives you starter access to create a basic campaign. Paid subscriptions unlock additional templates, higher limits, and premium features.'
      },
      {
        number: 2,
        title: 'Compare Paid Subscriptions',
        description: 'Click "Compare paid subscriptions" to view a side-by-side breakdown of what each tier offers, including pricing, campaign limits, and included features.'
      },
      {
        number: 3,
        title: 'Complete Checkout',
        description: 'If you choose a paid plan, you\'ll be taken to a secure Stripe checkout page to complete payment. Your subscription activates immediately after checkout.'
      }
    ],
    tips: [
      'You can start with the free plan and upgrade at any time.',
      'Stripe handles all payment processing securely — your card details are never stored on our servers.',
      'Paid subscriptions can be managed from your account billing page after sign-up.'
    ]
  },
  'quiz-config': {
    title: 'Quiz Help',
    overview: 'Choose a default quiz theme, change colours, edit questions, and schedule when questions display.',
    steps: [
      {
        number: 1,
        title: 'Pick a Template or Build Your Own',
        description: 'Choose from the default themes or create your own questions and answers under the Questions tab.'
      },
      {
        number: 2,
        title: 'Style and Schedule',
        description: 'Set the look of your questions and answers. Configure availability and question rotation under the distribution schedule.'
      }
    ],
    tips: [
      'Keep questions short and clear for mobile users.',
      'Use the distribution schedule to rotate questions over the campaign period.'
    ]
  }
};
